import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createResident,
  getResident,
  listResidents,
  updateResident,
  type Resident,
  type ResidentPayload,
} from "@/api/residents";

type UseResidentTrackOptions = {
  municipalityId?: number;
};

export function useResidentTrack({ municipalityId }: UseResidentTrackOptions) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedId(null);
  }, [municipalityId, debouncedSearch]);

  const listQueryKey = useMemo(
    () => ["residents", municipalityId, debouncedSearch] as const,
    [municipalityId, debouncedSearch],
  );

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: ({ signal }) =>
      listResidents(municipalityId!, { search: debouncedSearch || undefined, signal }),
    enabled: municipalityId != null,
  });

  const detailQuery = useQuery({
    queryKey: ["residents", municipalityId, "detail", selectedId] as const,
    queryFn: ({ signal }) => getResident(municipalityId!, selectedId!, signal),
    enabled: municipalityId != null && selectedId != null,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: ResidentPayload) => updateResident(municipalityId!, selectedId!, patch),
    onSuccess: (updated: Resident) => {
      queryClient.setQueryData(["residents", municipalityId, "detail", updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ["residents", municipalityId] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: ResidentPayload) => createResident(municipalityId!, payload),
    onSuccess: (created: Resident) => {
      queryClient.invalidateQueries({ queryKey: ["residents", municipalityId] });
      queryClient.setQueryData(["residents", municipalityId, "detail", created.id], created);
      setSelectedId(created.id);
    },
  });

  const residents = listQuery.data?.residents ?? [];
  const selected = detailQuery.data ?? null;

  return {
    search,
    setSearch,
    selectedId,
    setSelectedId,
    residents,
    selected,
    totalCount: listQuery.data?.total_count ?? 0,
    isLoading: listQuery.isLoading,
    isDetailLoading: detailQuery.isLoading,
    error: listQuery.error,
    updating: updateMutation.isPending,
    creating: createMutation.isPending,
    updateResident: (patch: ResidentPayload) => updateMutation.mutateAsync(patch),
    createResident: (payload: ResidentPayload) => createMutation.mutateAsync(payload),
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: ["residents", municipalityId] });
      if (selectedId != null) {
        queryClient.invalidateQueries({
          queryKey: ["residents", municipalityId, "detail", selectedId],
        });
      }
    },
  };
}
